import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ReviewFormProps {
  orderId: string;
  sellerId: string;
  onSubmitted: () => void;
}

const ReviewForm = ({ orderId, sellerId, onSubmitted }: ReviewFormProps) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({ title: "Note requise", description: "Veuillez donner une note.", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase.from("reviews").insert({
      order_id: orderId,
      reviewer_id: user.id,
      seller_id: sellerId,
      rating,
      comment,
    });

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Merci !", description: "Votre avis a été publié." });
      onSubmitted();
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-card rounded-lg border p-4 space-y-3">
      <p className="text-sm font-medium">Laisser un avis</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            onClick={() => setRating(s)}
            onMouseEnter={() => setHoverRating(s)}
            onMouseLeave={() => setHoverRating(0)}
            className="transition-transform hover:scale-110"
          >
            <Star
              className={`h-6 w-6 ${
                s <= (hoverRating || rating) ? "text-amber fill-current" : "text-muted-foreground"
              }`}
            />
          </button>
        ))}
      </div>
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, 400))}
        placeholder="Décrivez votre expérience (optionnel, max 400 caractères)..."
        rows={3}
        maxLength={400}
      />
      <p className="text-xs text-muted-foreground text-right">{comment.length}/400</p>
      <Button onClick={handleSubmit} disabled={submitting} size="sm">
        {submitting ? "Envoi..." : "Publier l'avis"}
      </Button>
    </div>
  );
};

export default ReviewForm;
